
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import { _ as _objectSpread, a as _slicedToArray } from './_rollupPluginBabelHelpers-mixin.js';
import { f as findNodeByContent, e as elementHasOverflow } from './table-auto-height-mixin.js';
import isFunction from 'lodash/isFunction';

const classifyRE = /(?:^|[-_])(\w)/g;

const classify = str => str.replace(classifyRE, cls => cls.toUpperCase()).replace(/[-_]/g, '');

function formatComponentName(vm, includeFile) {
  if (vm.$root === vm) {
    return '<Root>';
  }

  const options = typeof vm === 'function' && vm.cid != null ? vm.options : vm._isVue ? vm.$options || vm.constructor.options : vm || {};
  let name = options.name || options._componentTag;
  const file = options.__file;

  if (!name && file) {
    const match = file.match(/([^/\\]+)\.vue$/);
    name = match && match[1];
  }

  return (name ? `<${classify(name)}>` : '<Anonymous>') + (file && includeFile !== false ? ` at ${file}` : '');
}

function generateComponentTrace(vm) {
  if (vm._isVue && vm.$parent) {
    const tree = [];
    let currentRecursiveSequence = 0;

    while (vm) {
      if (tree.length > 0) {
        const last = tree[tree.length - 1];

        if (last.constructor === vm.constructor) {
          currentRecursiveSequence++;
          vm = vm.$parent;
          continue;
        } else if (currentRecursiveSequence > 0) {
          tree[tree.length - 1] = [last, currentRecursiveSequence];
          currentRecursiveSequence = 0;
        }
      }

      tree.push(vm);
      vm = vm.$parent;
    }

    return `\n\nfound in\n\n${tree.map((vm, i) => `${i === 0 ? '---> ' : ' '.repeat(5 + i * 2)}${Array.isArray(vm) ? `${formatComponentName(vm[0])}... (${vm[1]} recursive calls)` : formatComponentName(vm)}`).join('\n')}`;
  }

  return `\n\n(found in ${formatComponentName(vm)})`;
}

function createMessage(message, vm, parent) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }

  if (parent) {
    vm = {
      _isVue: true,
      $parent: parent,
      $options: vm
    };
  }

  if (vm) {
    vm.$_alreadyWarned = vm.$_alreadyWarned || [];

    if (vm.$_alreadyWarned.includes(message)) {
      return;
    }

    vm.$_alreadyWarned.push(message);
  }

  return `[Components] ${message}${vm ? generateComponentTrace(vm) : ''}`;
}
function warn(message, vm, parent) {
  const newMessage = createMessage(message, vm, parent);
  newMessage != null && console.warn(newMessage);
}

function isObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function isEmpty(value) {
  if (value === 0) {
    return false;
  }

  if (isObject(value)) {
    return !Object.keys(value).length;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  return !value;
}

function not(fn) {
  return (...params) => !fn(...params);
}

function includes(str, query) {
  if (str === undefined) {
    str = 'undefined';
  }

  if (str === null) {
    str = 'null';
  }

  if (str === false) {
    str = 'false';
  }

  const text = str.toString().toLowerCase();
  return text.includes(query.trim());
}

function flow(...fns) {
  return x => fns.reduce((v, fn) => fn(v), x);
}

function filterOptions(options, search, labelKey, formatOptionLabel) {
  return options.filter(option => includes(formatOptionLabel(option, labelKey), search));
}

function flattenOptions(groupOptionsKey, groupLabelKey, valueKey, groupSelect) {
  return options => options.reduce((prev, curr) => {
    if (curr[groupOptionsKey] && curr[groupOptionsKey].length) {
      const groupOptions = curr[groupOptionsKey].map(option => _objectSpread({
        type: 'option'
      }, option));
      let groupValueKey = curr[valueKey];
      const legacyGroupType = ['default', 'divider'].filter(label => [curr[groupLabelKey], groupValueKey].includes(label))[0];

      if (!groupValueKey && !legacyGroupType) {
        groupValueKey = curr[groupLabelKey].toLowerCase();
        warn(`Group for '${curr[groupLabelKey]}' does not contain a key matching the supplied 'valueKey' of '${valueKey}'. Make sure the group object has a '${valueKey}' key in it. Failure to include this key can affect rendering of the options list.`, this);
      }

      if (legacyGroupType) {
        if (legacyGroupType === 'divider' && prev.length && groupOptions[0]) {
          groupOptions[0].divided = true;
        }

        return prev.concat(groupOptions);
      }

      prev.push(_objectSpread({
        label: curr[groupLabelKey],
        [valueKey]: groupValueKey,
        disabled: !groupSelect
      }, curr, {
        type: 'group',
        [groupLabelKey]: curr[groupLabelKey],
        [groupOptionsKey]: groupOptions
      }));
      return prev.concat(curr[groupOptionsKey]);
    }

    return prev;
  }, []);
}

function filterGroups(search, labelKey, groupOptionsKey, groupLabelKey, formatOptionLabel) {
  return groups => groups.map(group => {
    const groupOptions = filterOptions(group[groupOptionsKey], search, labelKey, formatOptionLabel);
    return groupOptions.length ? _objectSpread({}, group, {
      [groupLabelKey]: group[groupLabelKey],
      [groupOptionsKey]: groupOptions
    }) : [];
  });
}

function stripGroups(options) {
  return options.filter(option => option.type !== 'group');
}

function flatAndStrip(groupOptionsKey, groupLabelKey, valueKey, options) {
  return flow(flattenOptions(groupOptionsKey, groupLabelKey, valueKey), stripGroups)(options);
}

function getNextNonDisabledIndex(moveAmount, baseIndex, options, groupSelect, circular) {
  const optionCount = options.length;
  const current = options[baseIndex];

  if (!current || !current.disabled) {
    return baseIndex;
  }

  if (moveAmount > 0) {
    for (let index = baseIndex + 1; index < optionCount; index++) {
      if (!options[index].disabled) {
        return index;
      }
    }
  } else {
    for (let index = baseIndex - 1; index >= 0; index--) {
      if (!options[index].disabled) {
        return index;
      }
    }
  }

  if (circular) {
    return moveAmount > 0 ? getNextNonDisabledIndex(1, 0, options) : getNextNonDisabledIndex(-1, optionCount - 1, options);
  }

  return -1;
}

function getNextWrappingIndex(moveAmount, baseIndex, options, groupSelect, circular = true) {
  const optionCount = options.length;
  const optionsLastIndex = optionCount - 1;

  if (typeof baseIndex !== 'number' || baseIndex < 0 || baseIndex >= optionCount) {
    baseIndex = moveAmount > 0 ? -1 : optionsLastIndex + 1;
  }

  let newIndex = baseIndex + moveAmount;

  if (newIndex < 0) {
    newIndex = circular ? optionsLastIndex : 0;
  } else if (newIndex > optionsLastIndex) {
    newIndex = circular ? 0 : optionsLastIndex;
  }

  const nonDisabledNewIndex = getNextNonDisabledIndex(moveAmount, newIndex, options, groupSelect, circular);

  if (nonDisabledNewIndex === -1) {
    return baseIndex;
  }

  return nonDisabledNewIndex;
}

const TRIGGER_SIZE_MAP = {
  medium: 36,
  small: 32,
  mini: 26
};

function getLongestOptionLabels(arr) {
  return arr.sort((a, b) => b.length - a.length).slice(0, 10);
}

function calculateMinMenuWidthFromLabels(labels, labelKey) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = '700 14px "Inter", Helvetica Neue, Arial, sans-serif';
  const longestOptionLabels = getLongestOptionLabels(labels);
  const widths = [];
  longestOptionLabels.forEach(label => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const _ctx$measureText = ctx.measureText(label),
          width = _ctx$measureText.width;

    widths.push(width);
  });
  return Math.max(...widths);
}

function shouldAttachOverflowTooltip(content, el) {
  const _findNodeByContent = findNodeByContent(content, el),
        _findNodeByContent2 = _slicedToArray(_findNodeByContent, 1),
        node = _findNodeByContent2[0];

  if (!node || !elementHasOverflow(node)) {
    return false;
  }

  return true;
}

const collator = new Intl.Collator('en', {
  numeric: true,
  sensitivity: 'base'
});

function compareBooleans(a, b) {
  return a === b ? 0 : a ? -1 : 1;
}

function sortData(a, b, vm) {
  let aValue;
  let bValue;

  if (vm.sortBy) {
    if (isFunction(vm.sortBy)) {
      aValue = vm.sortBy(a);
      bValue = vm.sortBy(b);
    } else {
      aValue = a[vm.sortBy];
      bValue = b[vm.sortBy];
    }
  } else if (vm.formatOptionLabel) {
    aValue = vm.formatOptionLabel(a, vm.labelKey);
    bValue = vm.formatOptionLabel(b, vm.labelKey);
  } else {
    aValue = a[vm.labelKey];
    bValue = b[vm.labelKey];
  }

  let result = 0;

  if (Boolean(aValue) === aValue && Boolean(bValue) === bValue) {
    result = compareBooleans(aValue, bValue);
  } else {
    result = collator.compare(aValue, bValue);
  }

  return result;
}

export { TRIGGER_SIZE_MAP as T, filterOptions as a, flattenOptions as b, isObject as c, flatAndStrip as d, calculateMinMenuWidthFromLabels as e, filterGroups as f, getNextWrappingIndex as g, shouldAttachOverflowTooltip as h, isEmpty as i, not as n, sortData as s, warn as w };
